import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BodyDataHistoryItem } from "../../services/bodyData.service";
import { useI18nStore } from "../../stores/i18nStore";
import {
  effectivePreferences,
  type UnitPreferences,
  useUnitPreferencesStore,
} from "../../stores/unitPreferencesStore";
import {
  formatChartDate,
  formatNumber,
  formatTooltipTimestamp,
} from "../../utils/dateLocalization";
import { kgToLb } from "../../utils/unitLocalization";
import type { Language } from "../../types/i18n";

interface BodyDataChartProps {
  data: BodyDataHistoryItem[];
  showBMI: boolean;
  showBodyFat: boolean;
  height: number;
}

/**
 * Recharts' built-in `dot` object prop is filtered down to known SVG
 * attributes, so a `data-testid` passed there never reaches the DOM. Rendering
 * the dot ourselves is the only way to expose each plotted point to e2e tests.
 */
const TaggedDot = (props: {
  cx?: number | undefined;
  cy?: number | undefined;
  fill?: string | undefined;
}) => {
  const { cx, cy, fill } = props;
  if (cx === undefined || cy === undefined) return null;
  return (
    <circle
      data-testid="chart-data-point"
      cx={cx}
      cy={cy}
      r={4}
      fill={fill}
      strokeWidth={2}
    />
  );
};

interface ChartDataPoint {
  date: string;
  weight?: number;
  bmi?: number | undefined;
  bodyFat?: number | undefined;
  formattedDate: string;
  timestamp: string;
  fhirTimestamp: string;
}

type WeightUnit = "kg" | "lb" | "st";

const KG_PER_STONE = 6.35029318;

/**
 * The weight unit the chart shows: the unit preferences' own rule
 * (`effectivePreferences`), mapped to the chart's labels.
 */
export function resolveWeightUnit(
  language: Language,
  useNationalityDefaults: boolean | undefined,
  customWeightUnit: string | undefined,
): WeightUnit {
  const weight = effectivePreferences(
    language,
    useNationalityDefaults,
    customWeightUnit
      ? { weight: customWeightUnit as UnitPreferences["weight"] }
      : null,
  ).weight;
  return weight === "[lb_av]" ? "lb" : weight === "[stone_av]" ? "st" : "kg";
}

function convertKg(kg: number, unit: WeightUnit): number {
  const value =
    unit === "lb" ? kgToLb(kg) : unit === "st" ? kg / KG_PER_STONE : kg;
  return Math.round(value * 10) / 10;
}

export const BodyDataChart: React.FC<BodyDataChartProps> = ({
  data,
  showBMI,
  showBodyFat,
  height,
}) => {
  const { t, language } = useI18nStore();
  const { useNationalityDefaults, preferences } = useUnitPreferencesStore();

  const weightUnit = resolveWeightUnit(
    language,
    useNationalityDefaults,
    preferences?.weight,
  );
  const weightUnitDisplay = weightUnit;

  // Stored weights are kg; convert to the display unit. Sorted by timestamp
  // so the line runs in chronological order.
  const convertedData: ChartDataPoint[] = useMemo(
    () =>
      [...data]
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        )
        .map((item) => {
          // Ignore non-positive weights: a stray 0/negative entry otherwise
          // drags the whole Y-axis below zero and the line plunges off-chart.
          const weightKg =
            typeof item.weight === "number" && item.weight > 0
              ? item.weight
              : undefined;
          return {
            date: item.date,
            weight:
              weightKg === undefined
                ? undefined
                : convertKg(weightKg, weightUnit),
            bmi: item.bmi,
            bodyFat: item.bodyFat,
            timestamp: item.timestamp,
            fhirTimestamp: formatTooltipTimestamp(item.timestamp, language),
            formattedDate: formatChartDate(item.timestamp, language),
          };
        }),
    [data, weightUnit, language],
  );

  const formatValue = (value: number) => formatNumber(value, language, 1);

  // Get the most recent entry (last in chronologically sorted array)
  const mostRecentEntry =
    convertedData.length > 0 ? convertedData[convertedData.length - 1] : null;
  const hasWeightData = convertedData.some(
    (entry) => typeof entry.weight === "number",
  );

  // Custom tooltip component with FHIR timestamp, i18n, and unit conversion
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: ChartDataPoint }>;
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      if (!data) return null;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">
            {data.fhirTimestamp}
          </p>
          <p className="text-xs text-gray-500 mb-2">
            {t.recorded}: {data.fhirTimestamp}
          </p>
          <div className="mt-2 space-y-1">
            {typeof data.weight === "number" && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-600">
                  {t.weight}: {formatValue(data.weight)} {weightUnitDisplay}
                </span>
              </div>
            )}
            {showBMI && data.bmi && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">
                  {t.bmiLabel}: {formatValue(data.bmi)}
                </span>
              </div>
            )}
            {showBodyFat && data.bodyFat && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-sm text-gray-600">
                  {t.bodyFat}: {formatValue(data.bodyFat)}%
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg"
        style={{ height }}
      >
        <div className="text-center text-gray-500">
          <div className="text-lg mb-2">📊</div>
          <p>{t.noWeightDataAvailable}</p>
          <p className="text-sm">{t.firstEntriesWillBeShown}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={convertedData}
          margin={{ top: 16, right: 8, left: 0, bottom: 16 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="formattedDate"
            stroke="#666"
            fontSize={12}
            tick={{ fill: "#666" }}
          />
          {hasWeightData && (
            <YAxis
              yAxisId="weight"
              domain={["dataMin - 2", "dataMax + 2"]}
              stroke="#3b82f6"
              fontSize={12}
              width={40}
              tick={{ fill: "#3b82f6" }}
            />
          )}
          {showBMI && (
            <YAxis
              yAxisId="bmi"
              orientation="right"
              domain={["dataMin - 1", "dataMax + 1"]}
              stroke="#10b981"
              fontSize={12}
              width={40}
              tick={{ fill: "#10b981" }}
            />
          )}
          {showBodyFat && (
            <YAxis
              yAxisId="bodyFat"
              orientation="right"
              domain={[0, 50]}
              stroke="#f97316"
              fontSize={12}
              width={40}
              tick={{ fill: "#f97316" }}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {hasWeightData && (
            <Line
              yAxisId="weight"
              type="monotone"
              dataKey="weight"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={<TaggedDot fill="#3b82f6" />}
              activeDot={{
                r: 6,
                stroke: "#3b82f6",
                strokeWidth: 2,
                fill: "#fff",
              }}
              name={`${t.weight} (${weightUnitDisplay})`}
              connectNulls={false}
            />
          )}

          {showBMI && (
            <Line
              yAxisId="bmi"
              type="monotone"
              dataKey="bmi"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
              activeDot={{
                r: 6,
                stroke: "#10b981",
                strokeWidth: 2,
                fill: "#fff",
              }}
              name={t.bmiLabel}
              connectNulls={false}
            />
          )}

          {showBodyFat && (
            <Line
              yAxisId="bodyFat"
              type="monotone"
              dataKey="bodyFat"
              stroke="#f97316"
              strokeWidth={2}
              dot={{ fill: "#f97316", strokeWidth: 2, r: 4 }}
              activeDot={{
                r: 6,
                stroke: "#f97316",
                strokeWidth: 2,
                fill: "#fff",
              }}
              name={t.bodyFatPercent}
              connectNulls={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Chart Summary */}
      <div className="mt-4 flex justify-between items-center text-sm text-gray-600 px-2">
        <div className="flex items-center space-x-4">
          <span>
            📈 {convertedData.length} {t.dataPoints}
          </span>
          {mostRecentEntry && (
            <>
              {typeof mostRecentEntry.weight === "number" && (
                <span>
                  {t.current}: {formatValue(mostRecentEntry.weight)}{" "}
                  {weightUnitDisplay}
                </span>
              )}
              {showBMI && mostRecentEntry.bmi && (
                <span>
                  {t.bmiLabel}: {formatValue(mostRecentEntry.bmi)}
                </span>
              )}
              {showBodyFat && mostRecentEntry.bodyFat && (
                <span>
                  {t.bodyFat}: {formatValue(mostRecentEntry.bodyFat)}%
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
